import { CanActivateFn, Router } from '@angular/router';
import { distinctUntilChanged, filter, first, firstValueFrom, map, of, race, switchMap } from 'rxjs';
import { authenticationActions, selectAuthentication } from '@redux/authentication.redux';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { corporationActions, corporationSelectors } from '@redux/corporation.effects';
import { Corporation } from '@models/customer/corporation.model';

export const authenticationGuard: CanActivateFn = () => {
  const token = localStorage.getItem('token');
  const router = inject(Router);
  const slug = localStorage.getItem('slug');
  const store = inject(Store);

  if (token) {
    return store.select(selectAuthentication.selectUser).pipe(
      switchMap((user) => {
        if (!user) {
          store.dispatch(authenticationActions.loadSession());
        }
        return store.select(selectAuthentication.selectUserAndCommunity).pipe(
          distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
          filter(({ user, community }) => !!user && !!community),
          switchMap(({ user, community }) => {
            if (user && community) {
              return of(true);
            }
            let path = '/404';
            if (slug) path = `/${slug}`;
            router.navigate([path]).then();
            return of(false);
          }),
        );
      }),
    );
  }

  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  
  if (!subdomain || subdomain === 'localhost' || subdomain === 'www') {
    router.navigate(['/404']).then();
    return of(false);
  }

  store.dispatch(corporationActions.read({
    path: 'corporations/get-by-field',
    params: { field: 'subdomain', value: subdomain }
  }));

  return firstValueFrom(
    race(
      store.select(corporationSelectors.selectModel).pipe(
        filter((corporation: Corporation | null) => !!corporation),
        first(),
        map((corporation: Corporation | null) => {
          if (corporation) {
            router.navigate([`/corporation/${corporation.id}/authentication/login`]).then();
            return false;
          } else {
            router.navigate(['/404']).then();
            return false;
          }
        }),
      ),
      store.select(corporationSelectors.selectError).pipe(
        filter((error: string | null) => !!error),
        first(),
        map(() => {
          router.navigate(['/404']).then();
          return false;
        }),
      ),
    ),
  );
};
